import {
	VelocityClient,
	UnifiedSwapClient,
	BulkAccountLoader,
} from '@velocity-exchange/sdk';
import { Connection } from '@solana/web3.js';
import { useCallback } from 'react';
import useVelocityStore from 'src/stores/VelocityStore/useVelocityStore';
import { RpcEndpoint } from '@velocity-exchange/common';
import Env from 'src/environmentVariables/EnvironmentVariables';

// The RPC's version never changes within a session — cache per endpoint so
// reconnects/retries against the same RPC don't re-fetch it.
const rpcVersionCache = new Map<
	string,
	Awaited<ReturnType<Connection['getVersion']>>
>();

interface HandleSubscriptionSuccessParams {
	selectedRpc: RpcEndpoint;
	newConnection: Connection;
	newAccountLoader: BulkAccountLoader;
	newVelocityClient: VelocityClient;
	newSwapClient: UnifiedSwapClient;
}

export const useHandleSubscriptionSuccess = () => {
	const setVelocityStore = useVelocityStore((s) => s.set);

	const handleSubscriptionSuccess = useCallback(
		({
			selectedRpc,
			newConnection,
			newAccountLoader,
			newVelocityClient,
			newSwapClient,
		}: HandleSubscriptionSuccessParams) => {
			setVelocityStore((s) => {
				s.connection.rpc = selectedRpc;
				s.connection.current = newConnection;
				s.connection.accountLoader = newAccountLoader;
				s.velocityClient.client = newVelocityClient;
				s.currentBulkAccountLoader = newAccountLoader;
				s.swapClient = newSwapClient;
			});

			const cachedVersion = rpcVersionCache.get(newConnection.rpcEndpoint);
			if (cachedVersion) {
				Env.rpcVersion = cachedVersion;
			} else {
				newConnection.getVersion().then((version) => {
					rpcVersionCache.set(newConnection.rpcEndpoint, version);
					Env.rpcVersion = version;
				});
			}
		},
		[setVelocityStore]
	);

	return { handleSubscriptionSuccess };
};
